/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useMemo, useState } from 'react';
import type { StaffClass, StaffGet, StaffTeacher, StaffTeacherClass, TeacherClassesAdd } from '@whizz/api-model/dist/@types';
import type { QueryObserverResult } from 'react-query';
import type { Cell, Column } from 'react-table';
import type { ThemeProps } from 'styled-components';
import type { ThemeType } from '../../../../../util/types';
import type { DataToEdit } from './AddEditInitialiseStaffModal';
import type { ToggleModalProps } from '../../../../../components/modal/types';
import type { AdditionalHeaderCellProps } from '../../../../../components/table/types';
import type Teacher from '@whizz/api-client/dist/@types/model/Teacher';
import { Button, Choice, Loader, Table } from '../../../../../components';
import { Edit, TableHeader } from './StaffTable.components';
import styled, { createGlobalStyle } from 'styled-components';
import { repository } from '@whizz/api-client';
import { useMutation } from 'react-query';
import { splitPhone } from '../../../../../util/phone';
import { DateTime } from 'luxon';
import { Translate, useI18n } from '@whizz/react-i18n';
import { useIdentity } from '@whizz/react-identity';
import { useModal } from '../../../../../hooks/useModal';
import { ModalFooter } from '../../../../../components/modal/Modal';
import { CancelButtonModal } from '../../../../../global';

const EMAIL_SUBSCRIBE_LIMIT_BEFORE_SHOWING_MODAL = 2; // Max number of classes the teacher has before the confirmation modal is shown
const LETTER_WIDTH = 11; // Magic number. Not perfect, but ok for now
const COLUMN_WIDTHS = {
  'admin': 50,
  'emailSubscribe': 50,
  'name': 200,
  'allNone': 50,
  'class': 50,
};

const GlobalStyle = createGlobalStyle<ThemeProps<ThemeType> & { $height: number }>`
  #staff {
    thead {
      tr:first-child {
        height: ${(props) => (props.$height * LETTER_WIDTH)}px;
      }
    },
    th {
      font-style: normal;
      font-size: 1.25rem;
    }
    [data-id^='col'] {
      > div {
        flex-direction: column;
        justify-content: flex-end;
        height: 100%;
      }
    }
    [data-id='col_name'] {
      > div {
        align-items: flex-start;
      }
    }
    [data-id='col_allNone'] {
      width: ${COLUMN_WIDTHS.allNone}px;
      min-width: ${COLUMN_WIDTHS.allNone}px;
      max-width: ${COLUMN_WIDTHS.allNone}px;
    }
    [data-id='col_admin'] {
      width: ${COLUMN_WIDTHS.admin}px;
      min-width: ${COLUMN_WIDTHS.admin}px;
      max-width: ${COLUMN_WIDTHS.admin}px;
    }
    [data-id='col_emailSubscribe'] {
      width: ${COLUMN_WIDTHS.emailSubscribe}px;
      min-width: ${COLUMN_WIDTHS.emailSubscribe}px;
      max-width: ${COLUMN_WIDTHS.emailSubscribe}px;
    }
    [data-id^='col_class'] {
      width: ${COLUMN_WIDTHS.class}px;
      min-width: ${COLUMN_WIDTHS.class}px;
      max-width: ${COLUMN_WIDTHS.class}px;
    }
    [data-id='col_emailSubscribe'],
    [data-column='cell_emailSubscribe'] {
      left: ${COLUMN_WIDTHS.admin}px !important;
    }
    [data-id='col_name'],
    [data-column='cell_name'] {
      width: ${COLUMN_WIDTHS.name}px;
      min-width: ${COLUMN_WIDTHS.name}px;
      max-width: ${COLUMN_WIDTHS.name}px;
      left: ${COLUMN_WIDTHS.admin + COLUMN_WIDTHS.emailSubscribe}px !important;
    }
    [data-id='col_allNone'],
    [data-column='cell_allNone'] {
      left: ${COLUMN_WIDTHS.admin + COLUMN_WIDTHS.emailSubscribe + COLUMN_WIDTHS.name}px !important;
    }
    [data-id='col_admin'],
    [data-id='col_emailSubscribe'] {
      font-weight: bold;
    }
    [data-id='col_allNone'],
    [data-column='cell_allNone'],
    [data-id='col_admin'],
    [data-column='cell_admin'],
    [data-id='col_emailSubscribe'],
    [data-column='cell_emailSubscribe'] {
      padding-left: 30px;
      padding-right: 30px;
    }
    [data-column='cell_admin'],
    [data-column='cell_emailSubscribe'],
    [data-column='cell_allNone'],
    [data-column^='cell_class'] {
      > div {
        display: flex;
        justify-content: center;
      }
    }
    [data-sticky-last-left-td="true"] {
      border-left: 1px solid ${(props) => props.theme.colors.muted};
      border-right: 1px solid ${(props) => props.theme.colors.muted};
    }
`;

const StaffTable = ({ onEdit, newStaff, staffQuery }: StaffTableProps): JSX.Element => {
  const [ formattedStaff, setFormattedStaff ] = useState<StaffTableItem[] | null>(null);
  const { translate } = useI18n();
  const { me } = useIdentity();
  const user = (me as Teacher);

  const [ confirmWeeklySubscriptionModal, toggleWeeklySubscriptionModal, setWeeklySubscriptionModalProps ] = useModal((props: ConfirmWeeklySubscriptionProps) => (
    <ConfirmWeeklySubscription {...props} />
  ));

  useEffect(() => {
    if (staffQuery.isRefetching) {
      setFormattedStaff(null);
    } else if (staffQuery.isSuccess) {
      setFormattedStaff(formatData(staffQuery.data));
    }
  }, [ staffQuery.isSuccess, staffQuery.isRefetching, staffQuery.data ]);

  const updateStaffPropsById = (id: number, updateStaff: (teacher: StaffTableItem) => void) => {
    setFormattedStaff((prev) => {
      const newFormattedStaff = [ ...(prev ?? []) ];

      const teacherIndex = newFormattedStaff.findIndex((teacher) => teacher.id === id);

      newFormattedStaff[teacherIndex] = {
        ...newFormattedStaff[teacherIndex],
      };

      updateStaff(newFormattedStaff[teacherIndex]);


      newFormattedStaff[teacherIndex].updatedAt = DateTime.now().toISO({ includeOffset: false });

      return newFormattedStaff;
    });
  };

  const mutations = {
    isAdmin: useMutation(
      repository.NewTeacherRepository.updateAdminRights,
      {
        onSuccess: (data, { teacherId, newAdminValue }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.admin = {
                loading: false,
                checked: newAdminValue,
              };
            },
          );
        },
        onError: (error, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.admin = {
                ...teacher.admin,
                loading: false,
              };
            },
          );
        },
      },
    ),
    emailSubscribe: useMutation(
      repository.NewTeacherRepository.updateSubscriptionWc,
      {
        onSuccess: (data, { teacherId, wantsToSubscribe }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.emailSubscribe = {
                loading: false,
                checked: wantsToSubscribe,
              };
            },
          );
        },
        onError: (error, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.emailSubscribe = {
                ...teacher.emailSubscribe,
                loading: false,
              };
            },
          );
        },
      },
    ),
    teacherClassAdd: useMutation(
      repository.TeacherClassRepository.addTeacherClass,
      {
        onSuccess: (data, { teacherId, classId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.classes[classId] = {
                checked: true,
                loading: false,
              };
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
        onError: (error, { teacherId, classId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.classes[classId] = {
                ...teacher.classes[classId],
                loading: false,
              };
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
      },
    ),
    teacherClassesAdd: useMutation(({ teacherId, classesIds }: TeacherClassesAdd ) => repository.TeacherClassRepository.addTeacherClasses({ teacherId, classesIds }),
      {
        onSuccess: (data, { teacherId, classesIds }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              classesIds.forEach((classId) => {
                teacher.classes[classId] = {
                  checked: true,
                  loading: false,
                };
              });
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
        onError: (data, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
      },
    ),
    teacherClassesDelete: useMutation(({ teacherId }: { teacherId: number } ) => repository.TeacherClassRepository.deleteTeacherClasses({ teacherId }),
      {
        onSuccess: (data, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              Object.keys(teacher.classes).forEach((classId) => {
                teacher.classes[Number(classId)] = {
                  checked: false,
                  loading: false,
                };
              });
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
        onError: (data, { teacherId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
      },
    ),
    teacherClassDelete: useMutation(
      repository.TeacherClassRepository.deleteTeacherClass,
      {
        onSuccess: (data, { teacherId, classId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.classes[classId] = {
                checked: false,
                loading: false,
              };
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
        onError: (error, { teacherId, classId }) => {
          updateStaffPropsById(
            teacherId,
            (teacher) => {
              teacher.classes[classId] = {
                ...teacher.classes[classId],
                loading: false,
              };
              teacher.allNone = {
                loading: false,
              };
            },
          );
        },
      },
    ),
  };

  const areThereSomeAdmins = formattedStaff && 1 < formattedStaff.filter((teacher) => teacher.isAdmin).length;

  const isLoadingData = (classes: StaffTableItem['classes']): boolean => {
    return !!Object.entries(classes).some((classObj) => classObj[1].loading);
  };

  const isAllChecked = (classes: StaffTableItem['classes']): boolean => {
    const findNotChecked = Object.entries(classes).some((classObj) => !classObj[1].checked);
    return !findNotChecked;
  };

  const howManyChecked = (classes: StaffTableItem['classes']): number => {
    const checked = Object.entries(classes).filter((classObj) => classObj[1].checked);
    return checked.length;
  };

  const columns: (Column<StaffTableItem> & AdditionalHeaderCellProps)[] = useMemo(() => [
    {
      id: 'admin',
      Header: <TableHeader title={translate('pages.ROSTER_MANAGEMENT.STAFF.ADMIN')} />,
      accessor: (originalRow) => originalRow.admin.checked ? 1 : 0,
      sticky: 'left',
      Cell: (cell: Cell<StaffTableItem>) => (
        !cell.row.original.admin.loading
          ? (
            <Choice
              id={`admin${cell.row.original.id}`}
              checked={cell.value}
              readOnly={!areThereSomeAdmins}
              disabled={cell.row.original.id === me?.id}
              onChange={(event) => updateIsAdmin(cell.row.original.id, event.target.checked)}
            />
          )
          : <Loader disableLabel smallSize={true} />
      ),
    },
    {
      id: 'emailSubscribe',
      Header: <TableHeader title={translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE')} />,
      accessor: (originalRow) => originalRow.emailSubscribe.checked ? 1 : 0,
      sticky: 'left',
      Cell: (cell: Cell<StaffTableItem>) => {
        const { id, email, username, emailSubscribe } = cell.row.original;
        const noEmail = !email || email !== username;
        return (
          !emailSubscribe.loading
            ? (
              <Choice
                id={'emailSubscribe'}
                checked={cell.value}
                disabled={noEmail}
                onChange={(event) => updateEmailSubscribe(id, event.target.checked)}
              />
            )
            : <Loader disableLabel smallSize={true} />
        );},
    },
    {
      id: 'name',
      accessor: 'onEditParams',
      sticky: 'left',
      Cell: (cell: Cell<StaffTableItem>) => {
        const { firstName, lastName } = cell.row.original;
        const name =  `${firstName} ${lastName}`;
        return <Edit label={name} onClick={onEdit ? () => onEdit(cell.row.original.onEditParams) : null} />;
      },
    },
    {
      id: 'allNone',
      Header: <TableHeader title={translate('pages.ROSTER_MANAGEMENT.STAFF.ALL_NONE')} />,
      sticky: 'left',
      Cell: (cell: Cell<StaffTableItem>) => (
        !cell.row.original.allNone.loading
          ? (
            <Choice
              checked={isAllChecked(cell.row.original.classes)}
              disabled={isLoadingData(cell.row.original.classes)}
              onChange={(event) => updateTeacherClasses(cell.row.original.id, event.target.checked)}
            />
          )
          : <Loader disableLabel smallSize={true} />
      ),
    },
    ...staffQuery.data!.classes.map((classObj: StaffClass) => ({
      id: `class${classObj.id}`,
      Header: <TableHeader title={classObj.name} />,
      accessor: (originalRow: StaffTableItem) => originalRow.classes[classObj.id].checked ? 1 : 0,
      title: translate('components.TABLES.SORT_BY', { sort: classObj.name }),
      Cell: (cell: Cell<StaffTableItem>) => (
        !cell.row.original.classes[classObj.id].loading
          ? (
            <Choice
              checked={cell.value}
              disabled={cell.row.original.allNone.loading}
              onChange={(event) => updateTeacherClass(cell.row.original.id, classObj.id, event.target.checked)}
            />
          )
          : <Loader disableLabel smallSize={true} />
      ),
    })),
  ], [ staffQuery.data!.classes, formattedStaff ]);

  const updateTeacherClass = (teacherId: number, classId: number, checking: boolean) => {
    updateStaffPropsById(
      teacherId,
      (teacher) => {
        teacher.classes[classId].loading = true;
      },
    );

    if (checking) {
      mutations.teacherClassAdd.mutate({ teacherId, classId });
    } else {
      mutations.teacherClassDelete.mutate({ teacherId, classId });
    }
  };

  const updateTeacherClasses = (teacherId: number, checking: boolean) => {
    const teacher = formattedStaff?.find((teacher) => teacher.id === teacherId);
    let loading = false;
    if (checking) {
      const classesIds = teacher?.classes ? Object.entries(teacher?.classes).filter((classObj) => !classObj[1].checked).map((classObj) => Number(classObj[0])) : null;
      if (classesIds) {
        loading = true;
        mutations.teacherClassesAdd.mutate({ teacherId, classesIds });
      }
    } else {
      loading = true;
      mutations.teacherClassesDelete.mutate({ teacherId });
    }
    updateStaffPropsById(
      teacherId,
      (teacher) => {
        teacher.allNone.loading = loading;
      },
    );
  };

  const updateIsAdmin = (teacherId: number, newAdminValue: boolean) => {

    if (teacherId === me?.id) {
      // Prevent self being able to alter their own admin rights
      return;
    }

    updateStaffPropsById(
      teacherId,
      (teacher) => {
        teacher.admin.loading = true;
      },
    );

    mutations.isAdmin.mutate({ teacherId, newAdminValue });
  };

  const updateEmailSubscribe = (teacherId: number, wantsToSubscribe: boolean) => {

    const doUpdate = () => {
      updateStaffPropsById(
        teacherId,
        (teacher) => {
          teacher.emailSubscribe.loading = true;
        },
      );
      mutations.emailSubscribe.mutate({ teacherId, wantsToSubscribe });
      toggleWeeklySubscriptionModal(false);
    };
    const teacher = formattedStaff?.find((teacher) => teacher.id === teacherId);
    if (teacher) {
      if (wantsToSubscribe) {
        const classCount = howManyChecked(teacher.classes);
        if (classCount > EMAIL_SUBSCRIBE_LIMIT_BEFORE_SHOWING_MODAL) {
          setWeeklySubscriptionModalProps({ title: translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.TITLE'), classCount, toggle: toggleWeeklySubscriptionModal, confirm: doUpdate });
          toggleWeeklySubscriptionModal(true);
          return;
        }
      }
      doUpdate();
    }
  };

  const formatData = (queryData: StaffGet): StaffTableItem[] | null => {
    if (queryData.teachers.length === 0) {
      return null;
    }
    return queryData.teachers.map((teacher: StaffTeacher) => {
      const classes = queryData.classes.reduce((acc, classObj: StaffClass) => {
        acc[classObj.id] = {
          checked: queryData.teachersClasses.some((tc: StaffTeacherClass) => tc.teacherId == teacher.id && tc.classId == classObj.id),
          loading: false,
        };

        return acc;
      }, {} as StaffTableItem['classes']);

      const [ phoneNationalCode, phoneNumber ] = splitPhone(teacher.phone);

      const onEditParams: DataToEdit = {
        firstName: teacher.firstName,
        lastName: teacher.lastName,
        id: teacher.id,
        email: teacher.email,
        username: teacher.username,
        recoveryEmail: teacher.recoveryEmail,
        jobType: teacher.jobType,
        jobTitle: teacher.jobTitle,
        phoneNationalCode: phoneNationalCode,
        phoneNumber: phoneNumber,
      };

      return {
        ...teacher,
        allNone: {
          checked: teacher.classesIds.length === queryData.classes.length,
          loading: false,
        },
        admin: {
          checked: teacher.isAdmin,
          loading: false,
        },
        emailSubscribe: {
          checked: teacher.isSubscribedToWeeklyUpdates,
          loading: false,
        },
        classes,
        onEditParams,
        updatedAt: DateTime.now().toISO({ includeOffset: false }),
      };
    });
  };

  if (!staffQuery.isFetched || staffQuery.isFetching || staffQuery.isRefetching || formattedStaff === null) {
    return (<Loader />);
  }

  const headerHeight = translate('pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE').length;

  const showWeeklyEmailColumn = user.country === 'gb';
  const filteredColumns = showWeeklyEmailColumn ? columns : columns.filter((col) => col.id !== 'emailSubscribe'); // remove weekly email subscibe for non-gb

  return (
    <StyledWrapper className={'bg-white rounded-default shadow p-md'}>
      <GlobalStyle $height={headerHeight} />
      {confirmWeeklySubscriptionModal}
      <Table
        id={'staff'}
        sticky
        truncate
        cols={filteredColumns}
        content={formattedStaff}
        initialState={{
          sortBy: [
            {
              id: 'name',
              desc: false,
            },
          ],
        }}
        disableSortRemove
        autoResetSortBy={false}
        newId={newStaff ?? undefined}
      />
      {showWeeklyEmailColumn ?
        <div className={'sticky bottom-0 p-md w-full bg-white z-max'}>
          <p className={'text-xs'}>
            <Translate i18nKey={'pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_INFO'}>
              { /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
              /* @ts-ignore */ }
              <strong />
            </Translate>
          </p>
        </div> : null
      }
    </StyledWrapper>
  );
};

const StyledWrapper = styled('div')`
  max-width: initial;
  width: fit-content;
`;

const ConfirmWeeklySubscription = ({ classCount, toggle, confirm }: ConfirmWeeklySubscriptionProps) => {
  const { translate } = useI18n();
  return (
    <>
      <div className={'container'}>
        <Translate i18nKey={'pages.ROSTER_MANAGEMENT.STAFF.EMAIL_SUBSCRIBE_MODAL.MESSAGE'} values={{ count: classCount }}>
          { /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          /* @ts-ignore */ }
          <strong />
        </Translate>
      </div>
      <ModalFooter>
        <CancelButtonModal onClick={() => toggle(false)}></CancelButtonModal>
        <Button
          isOutline
          color={'positive'}
          onClick={confirm}
        >{translate('components.BUTTONS.OK')}</Button>
      </ModalFooter>
    </>
  );
};

export default StaffTable;

interface StaffTableProps {
  onEdit: ((data: DataToEdit) => void) | null;
  staffQuery:  QueryObserverResult<StaffGet, unknown>;
  newStaff: number | null;
}

interface ConfirmWeeklySubscriptionProps extends Pick<ToggleModalProps, 'toggle'> {
  classCount: number;
  confirm: () => void;
}

interface StaffTableItem extends StaffTeacher {
  allNone: {
    loading: boolean;
  },
  id: number;
  firstName: string;
  lastName: string | null;
  admin: {
    checked: boolean;
    loading: boolean;
  };
  emailSubscribe: {
    checked: boolean;
    loading: boolean;
  };
  classes: {
    [id: number]: {
      checked: boolean;
      loading: boolean;
    };
  };
  onEditParams: DataToEdit;
  updatedAt: string;
}
